import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Radar, Phone } from "lucide-react";
import { getRiders } from "@/lib/api";

const COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6", "#06B6D4"];
const ago = (iso) => { const s = Math.max(0, Math.round((Date.now() - new Date(iso)) / 1000)); return s < 60 ? `${s}s ago` : `${Math.round(s / 60)} min ago`; };

export const RidersOnRoad = () => {
  const [riders, setRiders] = useState([]);
  const mapRef = useRef(null);
  const boxRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    const load = () => getRiders().then(setRiders).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!boxRef.current || riders.length === 0) return;
    if (!mapRef.current) {
      mapRef.current = L.map(boxRef.current, { scrollWheelZoom: false }).setView([-37.209, 175.873], 13);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(mapRef.current);
      layerRef.current = L.layerGroup().addTo(mapRef.current);
    }
    layerRef.current.clearLayers();
    const pts = [];
    riders.forEach((r, i) => {
      const c = COLORS[i % COLORS.length];
      L.circleMarker([r.lat, r.lng], { radius: 9, color: "#fff", weight: 2, fillColor: c, fillOpacity: 1 })
        .bindTooltip(`${r.rider_name || (r.role === "extra" ? "Secondary rider" : "Walk-in rider")} · ${r.route_title}`, { permanent: true, direction: "top", offset: [0, -8] })
        .addTo(layerRef.current);
      pts.push([r.lat, r.lng]);
    });
    if (pts.length) mapRef.current.fitBounds(L.latLngBounds(pts).pad(0.4), { maxZoom: 16 });
    setTimeout(() => mapRef.current?.invalidateSize(), 50);
  }, [riders]);

  return (
    <div className="border border-white/10 rounded-xl p-6" data-testid="riders-on-road-panel">
      <div className="flex items-center gap-2 mb-4"><Radar className="w-4 h-4 text-[#93C5FD]" /><p className="overline text-[#F5F5F0]/70">Riders on the road · {riders.length} live</p></div>
      {riders.length === 0 ? (
        <p className="text-sm text-[#F5F5F0]/40" data-testid="riders-empty">No one is out riding with Echo right now. Riders appear here once they activate a tour code and open the map.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div ref={boxRef} className="lg:col-span-3 h-72 lg:h-96 rounded-xl overflow-hidden border border-white/10" data-testid="riders-map" />
          <div className="lg:col-span-2 space-y-2 max-h-96 overflow-y-auto">
            {riders.map((r, i) => (
              <div key={`${r.route_id}-${r.device_id}`} className="rounded-xl border border-white/10 p-3 text-sm" data-testid={`rider-${r.device_id}`}>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} /><p className="font-medium truncate">{r.rider_name || (r.role === "extra" ? "Secondary rider" : "Walk-in / shared code")}</p></div>
                <p className="text-[#93C5FD] mt-1">{r.route_title} · stop {Math.min(r.stop_index + 1, r.stops_total)} of {r.stops_total}{r.navigating ? " · navigating" : ""}</p>
                <p className="text-xs text-[#F5F5F0]/50 font-mono mt-1">{ago(r.last_seen)} · {r.role === "extra" ? "secondary map" : "master map"}{r.booking_ref ? ` · ${r.booking_ref}` : ""}{r.code ? ` · ${r.code}` : ""}</p>
                <div className="flex gap-2 mt-2">
                  {r.rider_phone && <a data-testid={`rider-call-${r.device_id}`} href={`tel:${r.rider_phone}`} className="h-8 px-3 rounded-full bg-[#93C5FD] text-[#001A4D] text-xs flex items-center gap-1.5 font-medium"><Phone className="w-3.5 h-3.5" /> {r.rider_phone}</a>}
                  <a href={`https://www.google.com/maps?q=${r.lat},${r.lng}`} target="_blank" rel="noreferrer" className="h-8 px-3 rounded-full border border-white/20 text-xs flex items-center hover:bg-white/10">Open in Maps</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-xs text-[#F5F5F0]/35 mt-3">Riders are told in the waiver that their location is shared for health & safety while a tour code is active. Dots clear after 15 minutes without a signal; history is deleted after 7 days.</p>
    </div>
  );
};
